import { useState, useEffect } from 'react';
import { CONFIG } from '../config/variables';

// Module-level cache to prevent duplicate fetches across components
// Reduced TTL to 5 seconds so Sheet changes show quickly on reload
let _cmsCache = null;
let _cmsPromise = null;
let _lastFetchTime = 0;
const CACHE_TTL = 5000; // 5 seconds - reduced from 30s for faster Sheet sync

/**
 * Lightweight CMS hook that fetches public JSON from Google Apps Script.
 * - Non-blocking: homepage renders immediately with fallback content
 * - Module-level cache prevents duplicate network calls during same session
 * - Cache-busting with refresh=true&nocache=true ensures fresh data on each page load
 * - Graceful degradation: if fetch fails, fallback content remains
 */
export const useCMSData = () => {
  const [cmsData, setCmsData] = useState({ previews: null, seoLocations: null, synced: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cmsUrl = CONFIG.BPAS_CMS_URL;
    if (!cmsUrl || cmsUrl === 'TOBEUPDATED' || cmsUrl.trim() === '') {
      return;
    }

    const fetchCMSData = async () => {
      // Return cached data only if very recent (5 seconds)
      if (_cmsCache && Date.now() - _lastFetchTime < CACHE_TTL) {
        return { ..._cmsCache, synced: true };
      }

      // Return existing promise if fetch already in progress
      if (_cmsPromise) {
        return _cmsPromise;
      }

      setLoading(true);

      try {
        // Add cache-busting query parameters (refresh=true & nocache=true & clientTs)
        const separator = cmsUrl.includes('?') ? '&' : '?';
        const cacheBustedUrl = `${cmsUrl}${separator}refresh=true&nocache=true&clientTs=${Date.now()}`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);

        const response = await fetch(cacheBustedUrl, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' },
          cache: 'no-store'
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!data || typeof data !== 'object') {
          throw new Error('Invalid JSON structure');
        }

        // Normalize and validate previews array
        const previews = Array.isArray(data.previews) && data.previews.length > 0
          ? data.previews
              .filter(p => {
                const active = p.active ?? p.Active;
                return isActive(active);
              })
              .sort((a, b) => {
                const orderA = getSortOrder(a);
                const orderB = getSortOrder(b);
                return orderA - orderB;
              })
          : null;

        // Normalize and validate seoLocations array
        const seoLocations = Array.isArray(data.seoLocations) && data.seoLocations.length > 0
          ? data.seoLocations
              .filter(loc => {
                const active = loc.active ?? loc.Active;
                return isActive(active);
              })
              .sort((a, b) => {
                const orderA = getSortOrder(a);
                const orderB = getSortOrder(b);
                return orderA - orderB;
              })
          : null;

        const result = { previews, seoLocations };
        _cmsCache = result;
        _lastFetchTime = Date.now();
        _cmsPromise = null;

        return { ...result, synced: true };
      } catch (error) {
        console.debug('CMS fetch failed, using fallback content:', error.message);
        _cmsPromise = null;
        return { previews: null, seoLocations: null, synced: false };
      } finally {
        setLoading(false);
      }
    };

    _cmsPromise = fetchCMSData();
    _cmsPromise.then(result => {
      setCmsData(result);
    });
  }, []);

  return { ...cmsData, loading };
};

// Helper: Check if active value is truthy (handles "TRUE", "true", "Yes", "1", true, 1)
const isActive = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const str = String(value).toLowerCase().trim();
  return str === 'true' || str === 'yes' || str === '1';
};

// Helper: Get sort order (defaults to 999 if blank/missing)
const getSortOrder = (item) => {
  const order = item.sortOrder ?? item.SortOrder;
  if (order === null || order === undefined || order === '') return 999;
  return Number(order) || 999;
};